import {useCallback, useMemo} from "react";
import {useSearchParams} from "react-router-dom";
import {fromLocalDateString, toLocalDateString} from "@/utils/DateUtils";
import {isOnlyToday, isSingleDay, ReportPeriod} from "@widgets/reports/dayReport/period/ReportPeriod";

/** URL parameters with the period of the report, e.g. `?from=2026-11-01&to=2026-11-30`. */
const FROM_PARAM = "from";
const TO_PARAM = "to";

/** The day in the URL, or `null` when missing or not a valid `YYYY-MM-DD` date. */
const parseDay = (value: string | null): Date | null => {
    if (value === null || !/^\d{4}-\d{2}-\d{2}$/.test(value))
        return null;

    const day: Date = fromLocalDateString(value);
    // Rejects dates that roll over, like 2026-02-31
    return !isNaN(day.getTime()) && toLocalDateString(day) === value ? day : null;
};

/**
 * The selected period of the report, kept in the URL so it survives a
 * refresh and can be shared. Without (valid) parameters: today. Only `from`,
 * or `to` before `from`: that one day. Selecting only today removes the
 * parameters.
 */
const useReportPeriodParam = (): [ReportPeriod, (period: ReportPeriod) => void] => {
    const [searchParams, setSearchParams] = useSearchParams();
    const fromParam: string | null = searchParams.get(FROM_PARAM);
    const toParam: string | null = searchParams.get(TO_PARAM);
    const period: ReportPeriod = useMemo(() => {
        const from: Date = parseDay(fromParam) ?? new Date();
        const to: Date | null = parseDay(toParam);
        return {from, to: to === null || to < from ? from : to};
    }, [fromParam, toParam]);

    const setPeriod = useCallback((updated: ReportPeriod) => {
        setSearchParams(previous => {
            const next = new URLSearchParams(previous);
            next.delete(FROM_PARAM);
            next.delete(TO_PARAM);
            if (!isOnlyToday(updated)) {
                next.set(FROM_PARAM, toLocalDateString(updated.from));
                if (!isSingleDay(updated))
                    next.set(TO_PARAM, toLocalDateString(updated.to));
            }
            return next;
        }, {replace: true});
    }, [setSearchParams]);

    return [period, setPeriod];
};

export default useReportPeriodParam;